package pl.nickers.match3

import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.suspendCancellableCoroutine
import org.w3c.dom.events.Event
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.asList
import org.w3c.files.FileReader
import kotlin.coroutines.resume

private const val SAVE_FILENAME = "match3_save.json"

/** Browser-global URL utility — not exposed as a typed API in the Kotlin/JS stdlib. */
@JsName("URL")
private external object JsURL {
    fun createObjectURL(obj: dynamic): String
    fun revokeObjectURL(url: String)
}

/** Creates a JSON Blob from [content] using raw JS to bypass Kotlin/JS Blob constructor quirks. */
@Suppress("UNUSED_PARAMETER")
private fun jsonBlob(content: String): dynamic =
    js("new Blob([content], {type: 'application/json'})")

actual suspend fun exportGameToFile(json: String): Boolean {
    return try {
        val objectUrl = JsURL.createObjectURL(jsonBlob(json))
        val anchor = document.createElement("a").unsafeCast<HTMLAnchorElement>().apply {
            href = objectUrl
            download = SAVE_FILENAME
            style.display = "none"
        }
        document.body?.appendChild(anchor)
        anchor.click()
        document.body?.removeChild(anchor)
        JsURL.revokeObjectURL(objectUrl)
        true
    } catch (_: Throwable) {
        false
    }
}

actual suspend fun loadGameFromFile(): String? = suspendCancellableCoroutine { cont ->
    val input = document.createElement("input").unsafeCast<HTMLInputElement>().apply {
        type = "file"
        accept = ".json,application/json"
        style.display = "none"
    }

    input.onchange = {
        val file = input.files?.asList()?.firstOrNull()
        if (file == null) {
            cont.resume(null)
        } else {
            val reader = FileReader()
            reader.onload = { cont.resume(reader.result as? String) }
            reader.onerror = { cont.resume(null) }
            reader.readAsText(file)
        }
        Unit
    }

    // If the user dismisses the dialog the onchange event never fires;
    // use the window regaining focus as a fallback signal.
    var focusHandled = false
    window.addEventListener("focus", { _: Event ->
        if (!focusHandled) {
            focusHandled = true
            window.setTimeout({
                if (cont.isActive) cont.resume(null)
            }, 300)
        }
    })

    document.body?.appendChild(input)
    input.click()

    cont.invokeOnCancellation {
        runCatching { document.body?.removeChild(input) }
    }
}
